/**
 * The Garage: the rows, the labels and the panels behind the customization
 * screens.
 *
 * Pure, like ./progression-model and for the same reason — everything here is a
 * function of a `PlayerProfile` and a team colour, with no DOM, no storage, no
 * clock and no `Math.random`. `MenuModel` calls these to build its rows and
 * ./menu renders the markup; neither of them decides anything about what an
 * item IS, which is src/game/items.ts's job.
 *
 * ===========================================================================
 * TWO LOADOUTS, ONE PRESET
 * ===========================================================================
 *
 * Rocket League keeps a separate loadout for each team colour and so does this:
 * `Preset` carries a `blue` and an `orange`, `activeLoadout` picks one, and the
 * garage's team toggle decides which one every other row on the screen is about.
 * That is not a display filter — equipping a decal on blue genuinely leaves
 * orange alone, which is the whole point of the feature and is what the test
 * pins.
 *
 * ===========================================================================
 * WHAT A PICKER SHOWS
 * ===========================================================================
 *
 * Owned copies first, newest rarity down; then what the catalogue has and the
 * player has not, LOCKED. Showing the unowned matters — a slot that lists three
 * things gives no sense that there are forty — but showing all of them does not:
 * the number slot alone is a hundred items, and a screen you have to scroll for
 * ten seconds is a screen nobody reads. So the unowned tail is capped and the
 * remainder is stated as a count, which is honest about both.
 */

import {
  RARITY_ORDER,
  VISUAL_CAR_SLOTS,
  type CosmeticSlot,
  type InventoryItem,
  type ItemDef,
  type Loadout,
  type PlayerProfile,
  type Rarity,
} from '../game/cosmetics';
import { PAINT_HEX, RARITY_HEX, itemById, itemsForSlot } from '../game/items';
import {
  MAX_PRESETS,
  activeLoadout,
  activePreset,
  copiesOf,
  loadoutConflicts,
  resolvedItem,
} from '../game/inventory';
import { CAR_BODY_SPECS, type CarBodyId } from '../render/car-body';
import { liveryBody, type CarLivery, type LiveryLookup } from '../render/livery';
import { resolveLivery } from '../render/livery';
import type { MenuItem } from './menu-model';

// ---------------------------------------------------------------------------
// Slots
// ---------------------------------------------------------------------------

/**
 * The slots the garage edits, in the order they are shown.
 *
 * `VISUAL_CAR_SLOTS` from the contract, reordered so the two decisions that
 * change the car most — which body and what colour it is — are at the top, and
 * the two that are ours are at the bottom under their own heading in the panel.
 */
export const GARAGE_SLOTS: readonly CosmeticSlot[] = Object.freeze([
  'body',
  'paintFinish',
  'decal',
  'wheels',
  'boost',
  'trail',
  'topper',
  'antenna',
  'number',
  'flag',
] as const);

/** Every garage slot is a visual one; stated as an assertion, not a comment. */
export const UNRENDERED_VISUAL_SLOTS: readonly CosmeticSlot[] = Object.freeze(
  VISUAL_CAR_SLOTS.filter((s) => !GARAGE_SLOTS.includes(s))
);

export const SLOT_LABEL: Readonly<Record<CosmeticSlot, string>> = Object.freeze({
  body: 'Body',
  decal: 'Decal',
  paintFinish: 'Paint Finish',
  wheels: 'Wheels',
  boost: 'Boost',
  topper: 'Topper',
  antenna: 'Antenna',
  trail: 'Trail',
  goalExplosion: 'Goal Explosion',
  engineAudio: 'Engine Audio',
  banner: 'Banner',
  title: 'Title',
  avatarBorder: 'Avatar Border',
  number: 'Number',
  flag: 'Flag',
});

/** One line under a focused slot row: what the slot actually changes. */
export const SLOT_NOTE: Readonly<Partial<Record<CosmeticSlot, string>>> = Object.freeze({
  body: 'The car itself. Changes the hitbox as well as the shape.',
  paintFinish: 'How the paint answers a light: gloss, matte, metallic, pearlescent.',
  decal: 'Drawn into the body texture through the loft’s own UVs.',
  wheels: 'Rim style, spoke count and dish. The tyre size is the car’s, always.',
  boost: 'Flame colour and character. Painted variants recolour the whole flame.',
  trail: 'The streak you leave at supersonic.',
  topper: 'Sits on the roof. Changes the silhouette, so it cannot be a decal.',
  antenna: 'Stands on the rear deck.',
  number: 'A racing number on both doors, drawn the right way round on each.',
  flag: 'A country flag on the rear quarter, drawn from honest geometry.',
});

export const RARITY_LABEL: Readonly<Record<Rarity, string>> = Object.freeze({
  common: 'Common',
  uncommon: 'Uncommon',
  rare: 'Rare',
  veryRare: 'Very Rare',
  import: 'Import',
  exotic: 'Exotic',
  blackMarket: 'Black Market',
  limited: 'Limited',
});

/** Rarity colour as a CSS string, straight off the catalogue's own table. */
export const rarityTone = (rarity: Rarity): string =>
  `#${(RARITY_HEX[rarity] ?? 0x9aa4b2).toString(16).padStart(6, '0')}`;

export const paintTone = (paint: string): string => {
  const hex = PAINT_HEX[paint as keyof typeof PAINT_HEX];
  return hex === undefined ? '#9aa4b2' : `#${hex.toString(16).padStart(6, '0')}`;
};

// ---------------------------------------------------------------------------
// Row ids
// ---------------------------------------------------------------------------

export const GARAGE_SLOT_PREFIX = 'gslot:';
export const GARAGE_ITEM_PREFIX = 'gitem:';
export const GARAGE_PRESET_PREFIX = 'gpreset:';
/** The id the "nothing equipped" row carries. */
export const GARAGE_ITEM_NONE = `${GARAGE_ITEM_PREFIX}none`;

/**
 * How many not-owned rows a picker shows before it stops and says how many are
 * left. See the header — the number slot is a hundred items.
 */
export const LOCKED_ROW_LIMIT = 18;

// ---------------------------------------------------------------------------
// Colours
// ---------------------------------------------------------------------------

/**
 * The primary and accent choices, as '#rrggbb'.
 *
 * The thirteen painted variants plus the two team colours, which is deliberately
 * NOT a free colour picker: every other value in this front end is a list (see
 * the header of ./menu-model), and a hex field for a car colour would be the
 * only place a player could type something the drop tables have never heard of.
 */
export const TEAM_DEFAULT_COLORS: readonly [string, string] = ['#2a7dff', '#ff8a1f'];
export const TEAM_DEFAULT_ACCENTS: readonly [string, string] = ['#1d4fa8', '#a8571a'];

const hexOf = (v: number): string => `#${v.toString(16).padStart(6, '0')}`;

export interface ColorChoice {
  value: string;
  label: string;
}

export const colorChoices = (team: 0 | 1, accent: boolean): readonly ColorChoice[] => [
  { value: accent ? TEAM_DEFAULT_ACCENTS[team] : TEAM_DEFAULT_COLORS[team], label: 'Team' },
  ...(Object.keys(PAINT_HEX) as (keyof typeof PAINT_HEX)[]).map((paint) => ({
    value: hexOf(PAINT_HEX[paint]),
    label: paintLabel(paint),
  })),
];

/** 'titaniumWhite' -> 'Titanium White'. One rule rather than a second table. */
export const paintLabel = (paint: string): string =>
  paint.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

const colorIndex = (choices: readonly ColorChoice[], value: string): number => {
  const at = choices.findIndex((c) => c.value.toLowerCase() === value.toLowerCase());
  return at < 0 ? 0 : at;
};

export const cycleColor = (
  choices: readonly ColorChoice[],
  value: string,
  direction: 1 | -1
): string => {
  const at = colorIndex(choices, value);
  return choices[(at + direction + choices.length) % choices.length].value;
};

// ---------------------------------------------------------------------------
// Reading a loadout
// ---------------------------------------------------------------------------

/** The label a slot's row shows: the equipped item's name, or "None". */
export const equippedLabel = (profile: PlayerProfile, team: 0 | 1, slot: CosmeticSlot): string => {
  const resolved = resolvedItem(profile, team, slot);
  return resolved === null ? 'None' : resolved.def.name;
};

/** The body a team's loadout selects, for filtering every other slot's list. */
export const garageBody = (profile: PlayerProfile, team: 0 | 1): CarBodyId =>
  liveryBody(lookupFor(profile, team));

/** The `LiveryLookup` for one team's active loadout. */
export const lookupFor = (profile: PlayerProfile, team: 0 | 1): LiveryLookup => ({
  slot: (slot) => {
    const resolved = resolvedItem(profile, team, slot);
    return resolved === null ? null : { def: resolved.def, paint: resolved.inv.paint };
  },
  paintHex: PAINT_HEX,
});

/** The livery a team's loadout resolves to. What the preview and the match draw. */
export const garageLivery = (profile: PlayerProfile, team: 0 | 1): CarLivery =>
  resolveLivery(activeLoadout(profile, team), lookupFor(profile, team), team);

/**
 * The stock team car, for a preview with no profile behind it — the signed-out
 * main menu. Exactly what `buildCar` gives every non-player seat in a match:
 * team colours, no cosmetics, an Octane.
 */
export const stockLivery = (team: 0 | 1): CarLivery =>
  resolveLivery(
    { slots: {}, primaryColor: '', accentColor: '' },
    { slot: () => null, paintHex: PAINT_HEX },
    team
  );

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

const TEAM_NAME = ['Blue', 'Orange'] as const;

/** The garage's own rows: team, colours, every slot, and the presets. */
export const garageRows = (profile: PlayerProfile, team: 0 | 1): MenuItem[] => {
  const loadout = activeLoadout(profile, team);
  const body = garageBody(profile, team);
  const stale = loadoutConflicts(profile, team);
  const preset = activePreset(profile);

  const primary = colorChoices(team, false);
  const accent = colorChoices(team, true);

  const rows: MenuItem[] = [
    {
      kind: 'choice',
      id: 'garage-team',
      label: 'Team Colour',
      valueLabel: TEAM_NAME[team],
      index: team,
      count: 2,
      enabled: true,
      note: 'Two loadouts, as the real game has. Everything below is about this one.',
    },
    {
      kind: 'choice',
      id: 'garage-primary',
      label: 'Primary',
      valueLabel: primary[colorIndex(primary, loadout.primaryColor)].label,
      index: colorIndex(primary, loadout.primaryColor),
      count: primary.length,
      enabled: true,
      note: 'The body colour. The emissive team strips stay team-coloured whatever you pick.',
    },
    {
      kind: 'choice',
      id: 'garage-accent',
      label: 'Accent',
      valueLabel: accent[colorIndex(accent, loadout.accentColor)].label,
      index: colorIndex(accent, loadout.accentColor),
      count: accent.length,
      enabled: true,
      note: 'What a decal, a number and an unpainted topper are drawn in.',
    },
  ];

  for (const slot of GARAGE_SLOTS) {
    const resolved = resolvedItem(profile, team, slot);
    const conflicted = stale.includes(slot);
    rows.push({
      kind: 'value',
      id: `${GARAGE_SLOT_PREFIX}${slot}`,
      label: SLOT_LABEL[slot],
      valueLabel: resolved === null ? 'None' : resolved.def.name,
      badge: resolved === null ? undefined : RARITY_LABEL[resolved.def.rarity],
      badgeTone: resolved === null ? undefined : rarityTone(resolved.def.rarity),
      enabled: true,
      note: conflicted
        ? `That item does not fit the ${CAR_BODY_SPECS[body]?.label ?? body}. Pick another.`
        : SLOT_NOTE[slot],
    });
  }

  rows.push({
    kind: 'value',
    id: 'garage-presets',
    label: 'Presets',
    valueLabel: preset.name,
    badge: `${profile.presets.length} / ${MAX_PRESETS}`,
    enabled: true,
    note: 'A preset is a pair of loadouts, one per team colour.',
  });
  rows.push({ kind: 'action', id: 'back', label: 'Back', enabled: true });
  return rows;
};

/** One owned copy, or one catalogue entry the player has not got. */
interface PickerRow {
  def: ItemDef;
  inv: InventoryItem | null;
}

/**
 * The rows for one slot's picker.
 *
 * Sorted owned-first and then by rarity DESCENDING, because a picker sorted the
 * other way puts the player's Black Market wheels below eleven commons they have
 * never equipped.
 */
export const slotPickerRows = (
  profile: PlayerProfile,
  team: 0 | 1,
  slot: CosmeticSlot
): { rows: PickerRow[]; hidden: number } => {
  // The body slot is the one that must NOT be filtered by the equipped body:
  // every body item is restricted to itself, so filtering would leave a list of
  // exactly the body already on the car.
  const body = garageBody(profile, team);
  const catalogue = slot === 'body' ? itemsForSlot(slot) : itemsForSlot(slot, body);
  const rank = (r: Rarity): number => RARITY_ORDER.indexOf(r);

  const owned: PickerRow[] = [];
  const locked: PickerRow[] = [];
  for (const def of catalogue) {
    const copies = copiesOf(profile, def.id);
    if (copies.length === 0) locked.push({ def, inv: null });
    else for (const inv of copies) owned.push({ def, inv });
  }

  const byRarity = (a: PickerRow, b: PickerRow): number =>
    rank(b.def.rarity) - rank(a.def.rarity) || a.def.name.localeCompare(b.def.name);
  owned.sort(byRarity);
  locked.sort(byRarity);

  return {
    rows: [...owned, ...locked.slice(0, LOCKED_ROW_LIMIT)],
    hidden: Math.max(0, locked.length - LOCKED_ROW_LIMIT),
  };
};

/** The certification chip: "Striker 214", or nothing. */
export const certLabel = (inv: InventoryItem): string | undefined =>
  inv.certification === undefined
    ? undefined
    : `${paintLabel(inv.certification)} ${inv.certValue ?? 0}`;

export const slotRows = (
  profile: PlayerProfile,
  team: 0 | 1,
  slot: CosmeticSlot
): MenuItem[] => {
  const equipped = activeLoadout(profile, team).slots[slot] ?? null;
  const { rows, hidden } = slotPickerRows(profile, team, slot);

  const items: MenuItem[] = [
    {
      kind: 'value',
      id: GARAGE_ITEM_NONE,
      label: 'None',
      valueLabel: equipped === null ? 'Equipped' : '',
      enabled: true,
      emphasis: equipped === null,
      note: 'Take the slot off entirely.',
    },
  ];

  for (const row of rows) {
    const owned = row.inv !== null;
    const isEquipped = owned && row.inv!.instanceId === equipped;
    const badges: string[] = [RARITY_LABEL[row.def.rarity]];
    if (row.inv?.paint !== undefined) badges.push(paintLabel(row.inv.paint));
    const cert = row.inv ? certLabel(row.inv) : undefined;
    if (cert !== undefined) badges.push(cert);

    items.push({
      kind: 'value',
      id: owned ? `${GARAGE_ITEM_PREFIX}${row.inv!.instanceId}` : `${GARAGE_ITEM_PREFIX}locked:${row.def.id}`,
      label: row.def.name,
      valueLabel: isEquipped ? 'Equipped' : '',
      badge: badges.join(' · '),
      badgeTone: rarityTone(row.def.rarity),
      swatch: row.inv?.paint === undefined ? undefined : paintTone(row.inv.paint),
      enabled: owned,
      emphasis: isEquipped,
      lockLabel: 'Not Owned',
      note: owned
        ? undefined
        : `Not in your inventory yet. ${RARITY_LABEL[row.def.rarity]} · earned from drops, the pass or the shop.`,
    });
  }

  if (hidden > 0) {
    items.push({
      kind: 'value',
      id: 'garage-hidden',
      label: `… and ${hidden} more in the catalogue`,
      valueLabel: '',
      enabled: false,
      lockLabel: 'Hidden',
      note: 'The rest of this slot is in the Item Shop and the drop tables.',
    });
  }

  items.push({ kind: 'action', id: 'back', label: 'Back', enabled: true });
  return items;
};

export const presetRows = (profile: PlayerProfile): MenuItem[] => {
  const rows: MenuItem[] = profile.presets.map((preset) => ({
    kind: 'value' as const,
    id: `${GARAGE_PRESET_PREFIX}${preset.id}`,
    label: preset.name,
    valueLabel: preset.id === profile.activePresetId ? 'Active' : '',
    enabled: true,
    emphasis: preset.id === profile.activePresetId,
    note: 'Makes this pair of loadouts the one every match uses.',
  }));

  rows.push({
    kind: 'action',
    id: 'garage-preset-add',
    label: 'New Preset',
    enabled: profile.presets.length < MAX_PRESETS,
    lockLabel: 'Full',
    note:
      profile.presets.length < MAX_PRESETS
        ? 'A fresh pair of empty loadouts, and it becomes the active one.'
        : `${MAX_PRESETS} is the limit.`,
  });
  rows.push({ kind: 'action', id: 'back', label: 'Back', enabled: true });
  return rows;
};

// ---------------------------------------------------------------------------
// Panels
// ---------------------------------------------------------------------------

const esc = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * The summary beside the preview: what this loadout is, in one glance.
 *
 * Deliberately not a second copy of the rows — it is the two swatches, the body,
 * and the equipped items that are NOT on the rows the player is looking at, so
 * the panel earns its width rather than repeating the list next to it.
 */
export const garagePanelMarkup = (profile: PlayerProfile, team: 0 | 1): string => {
  const loadout: Loadout = activeLoadout(profile, team);
  const body = garageBody(profile, team);
  const spec = CAR_BODY_SPECS[body];

  const swatch = (label: string, color: string): string =>
    `<div class="rw-gswatch"><i style="background:${esc(color)}"></i>` +
    `<div><span>${esc(label)}</span><b>${esc(color.toUpperCase())}</b></div></div>`;

  const chips = GARAGE_SLOTS.filter((s) => s !== 'body')
    .map((slot) => {
      const resolved = resolvedItem(profile, team, slot);
      if (resolved === null) return '';
      return (
        `<div class="rw-gchip" style="--tone:${rarityTone(resolved.def.rarity)}">` +
        `<span>${esc(SLOT_LABEL[slot])}</span><b>${esc(resolved.def.name)}</b>` +
        (resolved.inv.paint === undefined
          ? ''
          : `<i style="background:${esc(paintTone(resolved.inv.paint))}"></i>`) +
        `</div>`
      );
    })
    .join('');

  return (
    `<div class="rw-garage-preview" data-garage-preview></div>` +
    `<div class="rw-gbar">` +
    swatch('Primary', loadout.primaryColor) +
    swatch('Accent', loadout.accentColor) +
    `<div class="rw-gswatch"><i class="rw-gbody"></i><div><span>Body</span>` +
    `<b>${esc(spec?.label ?? body)}</b></div></div>` +
    `</div>` +
    (chips ? `<div class="rw-gchips">${chips}</div>` : '') +
    `<div class="rw-ghint">Drag the car to turn it · it is the match renderer, not a picture</div>`
  );
};

/** The picker's panel: the preview, plus what this slot is and how many you own. */
export const slotPanelMarkup = (
  profile: PlayerProfile,
  team: 0 | 1,
  slot: CosmeticSlot
): string => {
  const { rows, hidden } = slotPickerRows(profile, team, slot);
  const owned = rows.filter((r) => r.inv !== null).length;
  const total = owned + rows.filter((r) => r.inv === null).length + hidden;
  const equipped = resolvedItem(profile, team, slot);

  const cell = (key: string, value: string): string =>
    `<div class="rw-stat"><div class="rw-stat-key">${esc(key)}</div>` +
    `<div class="rw-stat-val">${esc(value)}</div></div>`;

  return (
    `<div class="rw-garage-preview" data-garage-preview></div>` +
    `<div class="rw-stats">` +
    cell('Slot', SLOT_LABEL[slot]) +
    cell('Owned', `${owned} / ${total}`) +
    cell('Equipped', equipped?.def.name ?? 'None') +
    `</div>` +
    `<div class="rw-ghint">${esc(SLOT_NOTE[slot] ?? '')}</div>`
  );
};

export const presetPanelMarkup = (profile: PlayerProfile): string => {
  const rows = profile.presets
    .map((preset) => {
      const blue = countEquipped(preset.blue);
      const orange = countEquipped(preset.orange);
      return (
        `<div class="rw-gpreset${preset.id === profile.activePresetId ? ' on' : ''}">` +
        `<b>${esc(preset.name)}</b>` +
        `<span class="rw-gcount" data-team="0">Blue ${blue}</span>` +
        `<span class="rw-gcount" data-team="1">Orange ${orange}</span></div>`
      );
    })
    .join('');
  return `<div class="rw-gpresets">${rows}</div>`;
};

const countEquipped = (loadout: Loadout): number =>
  GARAGE_SLOTS.filter((slot) => {
    const id = loadout.slots[slot];
    if (id === null || id === undefined) return false;
    return true;
  }).length;

/** True when an instance id is one the player actually owns. Used by the model. */
export const ownsInstance = (profile: PlayerProfile, instanceId: string): boolean =>
  profile.inventory.some((i) => i.instanceId === instanceId);

/** The catalogue item behind an owned instance, or undefined. */
export const itemForInstance = (
  profile: PlayerProfile,
  instanceId: string
): ItemDef | undefined => {
  const inv = profile.inventory.find((i) => i.instanceId === instanceId);
  return inv === undefined ? undefined : itemById(inv.itemId);
};
